import { X, Undo } from 'lucide-react';

interface ReferenceCalibrationOverlayProps {
  points: { x: number; y: number }[];
  objectName: string;
  expectedLength: number;
  onVideoClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onCancel: () => void;
  onUndo?: () => void;
}

export function ReferenceCalibrationOverlay({
  points,
  objectName,
  expectedLength,
  onVideoClick,
  onCancel,
  onUndo,
}: ReferenceCalibrationOverlayProps) {
  return (
    <>
      {/* Full-screen clickable overlay */}
      <div
        className="absolute inset-0 z-[90] cursor-crosshair"
        onClick={onVideoClick}
        style={{ touchAction: 'none' }}
      >
        {/* Instruction banner at top */}
        <div className="absolute top-4 left-0 right-0 z-[91] flex justify-center px-4 pointer-events-none">
          <div className="bg-blue-600/95 backdrop-blur-md text-white px-6 py-3 rounded-full shadow-xl border border-blue-400/30 max-w-md">
            <div className="text-center">
              <div className="font-bold text-lg">
                {points.length === 0 ? `Tap one end of ${objectName}` : `Tap the other end`}
              </div>
              <div className="text-xs text-blue-100 mt-1">
                Expected length: {expectedLength}" ({(expectedLength * 2.54).toFixed(1)} cm)
              </div>
            </div>
          </div>
        </div>

        {/* Point markers */}
        {points.map((point, index) => (
          <div
            key={index}
            className="absolute w-5 h-5 bg-blue-500 border-3 border-white rounded-full shadow-lg z-[91] pointer-events-none animate-pulse"
            style={{
              left: `${point.x}%`,
              top: `${point.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded whitespace-nowrap">
              Point {index + 1}
            </div>
          </div>
        ))}

        {/* Line between points */}
        {points.length === 2 && (
          <svg
            className="absolute inset-0 w-full h-full z-[90] pointer-events-none"
            preserveAspectRatio="none"
          >
            <line
              x1={`${points[0].x}%`}
              y1={`${points[0].y}%`}
              x2={`${points[1].x}%`}
              y2={`${points[1].y}%`}
              stroke="#3b82f6"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="8,4"
            />
          </svg>
        )}
      </div>

      {/* Control buttons - bottom center with blur effect */}
      <div className="absolute bottom-24 left-0 right-0 z-[91] flex justify-center gap-3 px-4 pointer-events-auto">
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-full shadow-2xl px-4 py-3 flex items-center gap-2">
          {/* Cancel button */}
          <button
            onClick={onCancel}
            className="w-11 h-11 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition-all shadow-lg"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Undo button - only show when there's a point to undo */}
          {points.length > 0 && onUndo && (
            <button
              onClick={onUndo}
              className="w-11 h-11 rounded-full flex items-center justify-center bg-gray-600 hover:bg-gray-700 text-white transition-all shadow-lg"
            >
              <Undo className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
